import React, { useState, useEffect, Suspense, lazy, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PremiumLock from '../components/PremiumLock';
import { toast } from 'sonner';
import { BookOpen, Filter, Crown, ChevronRight, ChevronDown, CheckCircle, Check, Clock, Target, AlertTriangle, ListChecks, X, Dumbbell, Music } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import TabNotation from '../components/TabNotation';

// VexFlow is ~700KB gzip - only load it when the user opens a lesson
// that actually contains a music notation fence block. All other pages
// of the app (recorder, drums, songs) never pull VexFlow in.
const MusicNotation = lazy(() => import('../components/MusicNotation'));
// AudioExample uses soundfont-player (already pulled by the chord
// library) plus a small theory-audio hook. Lazy to keep the initial
// TheoryPage paint instant; loads as soon as a lesson with one opens.
const AudioExample = lazy(() => import('../components/AudioExample'));

// Detect a fenced code block that looks like a 6-string guitar tab.
const looksLikeTab = (text) => /^[eBGDAE]\|/m.test(text || '');

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// One reusable collapsible block. Used for the colored side-boxes
// (objectives, common mistakes, checklist, assigned exercises/songs).
// The `wrap`, `header`, and `chevron` class strings let each call site
// keep its own colour scheme without dynamic Tailwind compilation.
const CollapsibleSection = ({
  wrap,
  header,
  chevron = 'text-zinc-500',
  icon: Icon,
  title,
  children,
  testId,
  defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={wrap} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-3 ${open ? 'mb-4' : ''}`}
        aria-expanded={open}
      >
        <div className={`flex items-center gap-2 ${header}`}>
          {Icon ? <Icon className="w-5 h-5 shrink-0" /> : null}
          <h3 className="font-heading font-bold uppercase tracking-wider text-sm text-left">
            {title}
          </h3>
        </div>
        <ChevronDown
          className={`w-4 h-4 shrink-0 ${chevron} transition-transform duration-200 ${open ? '' : '-rotate-90'}`}
        />
      </button>
      {open && children}
    </div>
  );
};

// Collapsible wrapper for each H2 section inside a lesson markdown body.
// Inherits the existing H2 visual (purple accent bar on the left) so it
// looks like part of the text flow, not a popup card.
const MarkdownSection = ({ title, children, index }) => {
  const [open, setOpen] = useState(true);
  return (
    <section className="mt-10" data-testid={`section-md-${index}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-3 ${open ? 'mb-4' : ''}`}
        aria-expanded={open}
      >
        <h2 className="font-heading text-2xl font-bold text-white tracking-tight relative pl-4 text-left before:content-[''] before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-1 before:rounded-full before:bg-gradient-to-b before:from-purple-400 before:to-purple-600">
          {title}
        </h2>
        <ChevronDown
          className={`w-5 h-5 shrink-0 text-zinc-500 transition-transform duration-200 ${open ? '' : '-rotate-90'}`}
        />
      </button>
      {open && children}
    </section>
  );
};

// Split a markdown body into (intro, [sections]) at line-leading "## ".
// Drops the leading H1 because the lesson title is already shown in the
// hero header. Returns sections as { title, body } pairs.
const splitMarkdownByH2 = (markdown) => {
  if (!markdown) return { intro: '', sections: [] };
  const stripped = markdown.replace(/^#\s+[^\n]*\n+/, '');
  const parts = stripped.split(/^##\s+/m);
  const intro = (parts[0] || '').trim();
  const sections = parts.slice(1).map((p) => {
    const newlineIdx = p.indexOf('\n');
    if (newlineIdx === -1) return { title: p.trim(), body: '' };
    return {
      title: p.slice(0, newlineIdx).trim(),
      body: p.slice(newlineIdx + 1),
    };
  });
  return { intro, sections };
};

// Lesson IDs follow a stable shape (e.g. beginner-22-ottava). When prose
// references one as inline code, we turn it into a button that opens
// the target lesson in the same modal.
const LESSON_ID_RE = /^(beginner|intermediate|advanced|master|history)-\d+-[a-z0-9-]+$/;

// React-markdown components shared between the intro and every section.
// makeMarkdownComponents lets the live `lessons` array and openLesson
// handler reach the inline-code renderer so cross-references become
// clickable.
const makeMarkdownComponents = ({ lessons, openLesson }) => ({
  h1: ({ node, ...p }) => <h2 className="font-heading text-3xl font-black text-white mt-10 mb-5 tracking-tight" {...p} />,
  h2: ({ node, ...p }) => (
    <h3 className="font-heading text-2xl font-bold text-white mt-10 mb-4 tracking-tight relative pl-4 before:content-[''] before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-1 before:rounded-full before:bg-gradient-to-b before:from-purple-400 before:to-purple-600" {...p} />
  ),
  h3: ({ node, ...p }) => <h4 className="font-heading text-xl font-semibold text-purple-200 mt-8 mb-3" {...p} />,
  p: ({ node, ...p }) => <p className="my-5" {...p} />,
  ul: ({ node, ...p }) => <ul className="my-5 space-y-2 pl-1" {...p} />,
  ol: ({ node, ...p }) => <ol className="my-5 space-y-2 list-decimal list-inside marker:text-purple-400 marker:font-semibold" {...p} />,
  li: ({ node, ordered, ...p }) =>
    ordered ? (
      <li className="leading-relaxed pl-2" {...p} />
    ) : (
      <li className="leading-relaxed flex items-start gap-3 before:content-[''] before:shrink-0 before:mt-2.5 before:w-1.5 before:h-1.5 before:rounded-full before:bg-purple-400" {...p} />
    ),
  strong: ({ node, ...p }) => <strong className="text-white font-semibold" {...p} />,
  em: ({ node, ...p }) => <em className="text-purple-200 not-italic font-medium" {...p} />,
  a: ({ node, ...p }) => <a className="text-blue underline decoration-blue/40 hover:decoration-blue underline-offset-4" target="_blank" rel="noreferrer" {...p} />,
  hr: () => <hr className="my-10 border-white/5" />,
  code: ({ node, inline, children, ...p }) => {
    if (inline) {
      const text = Array.isArray(children) ? children.join('') : String(children || '');
      if (LESSON_ID_RE.test(text.trim())) {
        const target = lessons?.find?.((l) => l.id === text.trim());
        if (target && openLesson) {
          return (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); openLesson(target); }}
              className="bg-purple-500/10 hover:bg-purple-500/20 text-purple-300 hover:text-purple-100 rounded-md px-1.5 py-0.5 font-mono text-[0.92em] border border-purple-500/30 underline decoration-purple-400/50 hover:decoration-purple-200 underline-offset-2 transition-colors cursor-pointer"
              title={target.title_it || target.title}
            >
              {text}
            </button>
          );
        }
      }
      return <code className="bg-white/[0.07] text-amber rounded-md px-1.5 py-0.5 font-mono text-[0.92em] border border-white/5" {...p}>{children}</code>;
    }
    return <code className="block bg-[#0A0A0A] text-amber rounded-xl p-5 font-mono text-sm overflow-x-auto border border-white/5" {...p}>{children}</code>;
  },
  pre: ({ children, ...p }) => {
    const codeChildren = children?.props?.children;
    const codeClassName = children?.props?.className || '';
    const text = Array.isArray(codeChildren) ? codeChildren.join('') : String(codeChildren || '');
    if (codeClassName.includes('language-vexflow')) {
      return (
        <Suspense fallback={<div className="my-6 rounded-xl bg-[#0A0A0A] border border-white/5 p-4 text-zinc-500 text-sm">...</div>}>
          <MusicNotation>{text.replace(/\n$/, '')}</MusicNotation>
        </Suspense>
      );
    }
    if (codeClassName.includes('language-audio-example')) {
      return (
        <Suspense fallback={<div className="my-4 text-zinc-500 text-sm">...</div>}>
          <AudioExample>{text.replace(/\n$/, '')}</AudioExample>
        </Suspense>
      );
    }
    if (looksLikeTab(text)) {
      return <TabNotation>{text.replace(/\n$/, '')}</TabNotation>;
    }
    return <pre className="my-6 not-prose" {...p}>{children}</pre>;
  },
  blockquote: ({ node, ...p }) => (
    <blockquote className="my-6 rounded-2xl bg-blue/5 border-l-4 border-blue p-5 text-zinc-300 italic [&>p]:my-0" {...p} />
  ),
  table: ({ node, ...p }) => <div className="my-6 overflow-x-auto rounded-xl border border-white/5"><table className="w-full text-sm" {...p} /></div>,
  th: ({ node, ...p }) => <th className="bg-white/5 text-left font-semibold text-white px-4 py-3 border-b border-white/10" {...p} />,
  td: ({ node, ...p }) => <td className="px-4 py-3 border-b border-white/5" {...p} />,
});

const TheoryPage = () => {
  const { t, i18n } = useTranslation();
  const { isPremium, isAuthenticated } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLesson, setSelectedLesson] = useState(null);
  // Track which materia chapters are expanded. Default closed - the
  // page becomes a compact index of materie with counts; user clicks
  // to drill into a chapter.
  const [openChapters, setOpenChapters] = useState({});
  const toggleChapter = (key) =>
    setOpenChapters((o) => ({ ...o, [key]: !o[key] }));
  const [completedIds, setCompletedIds] = useState(new Set());
  const [savingId, setSavingId] = useState(null);
  const [lessonLoading, setLessonLoading] = useState(false);
  const [exercisesById, setExercisesById] = useState({});
  const [songsById, setSongsById] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get('focus');

  useEffect(() => {
    fetchLessons();
    fetchRelatedCatalogs();
    if (isAuthenticated) {
      fetchProgress();
    }
  }, [isAuthenticated]);

  // Load the lightweight exercise + song lists once so the lesson detail
  // can resolve assigned_exercises / assigned_songs ids to titles without
  // N+1 detail fetches.
  const fetchRelatedCatalogs = async () => {
    try {
      const [exRes, songRes] = await Promise.all([
        axios.get(`${API}/exercises`),
        axios.get(`${API}/songs`),
      ]);
      setExercisesById(Object.fromEntries((exRes.data || []).map((e) => [e.id, e])));
      setSongsById(Object.fromEntries((songRes.data || []).map((s) => [s.id, s])));
    } catch (error) {
      console.error('Error fetching related catalogs:', error);
    }
  };

  useEffect(() => {
    if (!focusId || lessons.length === 0 || selectedLesson) return;
    const target = lessons.find(l => l.id === focusId);
    if (target) {
      openLesson(target);
      const next = new URLSearchParams(searchParams);
      next.delete('focus');
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, lessons]);

  const fetchLessons = async () => {
    try {
      const response = await axios.get(`${API}/lessons`);
      setLessons(response.data);
    } catch (error) {
      console.error('Error fetching lessons:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${API}/progress?item_type=lesson`);
      const completed = new Set(
        response.data.filter(p => p.status === 'completed').map(p => p.item_id)
      );
      setCompletedIds(completed);
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  // /lessons (list) omits content/content_it for payload size; fetch the
  // full lesson by id when the user opens it.
  const openLesson = async (lesson) => {
    if (lesson.is_premium && !isPremium) return;
    setSelectedLesson(lesson);
    if (lesson.content || lesson.content_it) return;
    setLessonLoading(true);
    try {
      const response = await axios.get(`${API}/lessons/${lesson.id}`);
      setSelectedLesson(response.data);
    } catch (error) {
      console.error('Error fetching lesson:', error);
    } finally {
      setLessonLoading(false);
    }
  };

  const markdownComponents = useMemo(
    () => makeMarkdownComponents({ lessons, openLesson }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lessons]
  );

  const markCompleted = async (e, lessonId) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.error(t('theory.saveLogin'));
      return;
    }

    setSavingId(lessonId);
    try {
      await axios.post(`${API}/progress?item_type=lesson&item_id=${lessonId}&status=completed`);
      setCompletedIds(prev => new Set([...prev, lessonId]));
      toast.success(t('theory.completed'));
    } catch (error) {
      console.error('Error saving progress:', error);
      toast.error(t('theory.saveError'));
    } finally {
      setSavingId(null);
    }
  };

  const filteredLessons = lessons.filter(lesson => {
    if (selectedDifficulty !== 'all' && lesson.difficulty !== selectedDifficulty) return false;
    if (selectedSubject !== 'all' && lesson.subject !== selectedSubject) return false;
    if (selectedCategory !== 'all' && lesson.category !== selectedCategory) return false;
    return true;
  });

  // Categories within the currently-selected subject (or all). Lets the
  // Category dropdown narrow down once the user picks a Materia.
  const visibleForCategory = selectedSubject === 'all'
    ? lessons
    : lessons.filter(l => l.subject === selectedSubject);
  const categories = [...new Set(visibleForCategory.map(l => l.category).filter(Boolean))];
  const subjects = [...new Set(lessons.map(l => l.subject).filter(Boolean))];
  const difficulties = ['beginner', 'intermediate', 'advanced', 'master'];

  // Canonical materia order from CURRICULUM.md section 2.
  const SUBJECT_ORDER = ['history', 'theory', 'technique', 'style', 'gear', 'practice'];
  const DIFF_RANK = { beginner: 0, intermediate: 1, advanced: 2, master: 3, expert: 4 };
  const sortInSubject = (arr) =>
    [...arr].sort((a, b) => {
      const da = DIFF_RANK[a.difficulty] ?? 99;
      const db = DIFF_RANK[b.difficulty] ?? 99;
      if (da !== db) return da - db;
      return (a.order ?? 0) - (b.order ?? 0);
    });
  // Chapters: only when no specific subject filter is active. Each
  // chapter groups its lessons (sorted by difficulty then order) and is
  // skipped when empty under the current filters.
  const groupedChapters = selectedSubject === 'all'
    ? SUBJECT_ORDER
        .map(s => ({ subject: s, lessons: sortInSubject(filteredLessons.filter(l => l.subject === s)) }))
        .filter(c => c.lessons.length > 0)
    : null;

  const getTitle = (lesson) => i18n.language === 'it' ? lesson.title_it : lesson.title;
  const getContent = (lesson) => i18n.language === 'it' ? lesson.content_it : lesson.content;
  const getObjectives = (lesson) =>
    (i18n.language === 'it' ? lesson.objectives_it : lesson.objectives) || [];
  const getCommonMistakes = (lesson) =>
    (i18n.language === 'it' ? lesson.common_mistakes_it : lesson.common_mistakes) || [];
  const getChecklist = (lesson) =>
    (i18n.language === 'it' ? lesson.checklist_it : lesson.checklist) || [];
  const findLesson = (id) => lessons.find(l => l.id === id);

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12 px-6 md:px-12" data-testid="theory-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-black" />
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white">
              {t('theory.title')}
            </h1>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap gap-4 mb-8"
        >
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-400" />
            <span className="text-zinc-400 text-sm">{t('common.filter')}:</span>
          </div>
          
          <Select value={selectedDifficulty} onValueChange={setSelectedDifficulty}>
            <SelectTrigger className="w-40 bg-[#121212] border-white/10 text-white" data-testid="difficulty-filter">
              <SelectValue placeholder={t('theory.difficulty')} />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              <SelectItem value="all">{t('common.all')}</SelectItem>
              {difficulties.map(d => (
                <SelectItem key={d} value={d}>{t(`levels.${d}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedSubject} onValueChange={(v) => { setSelectedSubject(v); setSelectedCategory('all'); }}>
            <SelectTrigger className="w-40 bg-[#121212] border-white/10 text-white" data-testid="subject-filter">
              <SelectValue placeholder={t('theory.subject')} />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              <SelectItem value="all">{t('common.all')}</SelectItem>
              {subjects.map(s => (
                <SelectItem key={s} value={s}>{t(`theory.subjects.${s}`, { defaultValue: s })}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-40 bg-[#121212] border-white/10 text-white" data-testid="category-filter">
              <SelectValue placeholder={t('theory.category')} />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              <SelectItem value="all">{t('common.all')}</SelectItem>
              {categories.map(c => (
                <SelectItem key={c} value={c}>{t(`theory.categories.${c}`, { defaultValue: c })}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </motion.div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Lessons List */}
        {!loading && (() => {
          const renderCard = (lesson, index) => (
            <motion.div
              key={lesson.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.03 }}
              className="relative bg-[#121212] border border-white/5 rounded-xl p-6 hover:border-purple-500/30 transition-all group cursor-pointer"
              onClick={() => openLesson(lesson)}
              data-testid={`lesson-card-${lesson.id}`}
            >
              {lesson.is_premium && !isPremium && <PremiumLock />}

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-heading font-bold text-lg ${
                    lesson.difficulty === 'beginner' ? 'bg-green-500/20 text-green-400' :
                    lesson.difficulty === 'intermediate' ? 'bg-blue/20 text-blue' :
                    'bg-purple-500/20 text-purple-400'
                  }`}>
                    {lesson.order}
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-white flex items-center gap-2">
                      {getTitle(lesson)}
                      {lesson.is_premium && <Crown className="w-4 h-4 text-amber" />}
                      {completedIds.has(lesson.id) && <CheckCircle className="w-4 h-4 text-green-400" />}
                    </h3>
                    <p className="text-zinc-500 text-sm">
                      {t(`theory.categories.${lesson.category}`, { defaultValue: lesson.category })} • {t(`levels.${lesson.difficulty}`)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {(!lesson.is_premium || isPremium) && !completedIds.has(lesson.id) && (
                    <Button
                      onClick={(e) => markCompleted(e, lesson.id)}
                      disabled={savingId === lesson.id}
                      variant="ghost"
                      size="sm"
                      className="text-green-400 hover:bg-green-500/10"
                      data-testid={`mark-complete-${lesson.id}`}
                    >
                      {savingId === lesson.id ? (
                        <div className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                  <ChevronRight className="w-5 h-5 text-zinc-500 group-hover:text-white transition-colors" />
                </div>
              </div>
            </motion.div>
          );

          if (groupedChapters) {
            return (
              <div className="space-y-4">
                {groupedChapters.map((chapter, chapterIdx) => {
                  const open = !!openChapters[chapter.subject];
                  return (
                    <section key={chapter.subject} data-testid={`chapter-${chapter.subject}`}>
                      <motion.button
                        type="button"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: chapterIdx * 0.04 }}
                        onClick={() => toggleChapter(chapter.subject)}
                        aria-expanded={open}
                        className={`w-full flex items-baseline justify-between gap-3 border-b border-white/5 pb-3 text-left hover:border-purple-500/30 transition-colors ${open ? 'mb-4' : ''}`}
                      >
                        <div className="flex items-baseline gap-3">
                          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white">
                            {t(`theory.subjects.${chapter.subject}`, { defaultValue: chapter.subject })}
                          </h2>
                          <span className="text-zinc-500 text-sm font-mono">
                            {chapter.lessons.length}
                          </span>
                        </div>
                        <ChevronDown
                          className={`w-5 h-5 text-zinc-500 transition-transform duration-200 self-center ${open ? '' : '-rotate-90'}`}
                        />
                      </motion.button>
                      {open && (
                        <div className="space-y-4">
                          {chapter.lessons.map((lesson, i) => renderCard(lesson, i))}
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            );
          }
          return (
            <div className="space-y-4">
              {filteredLessons.map((lesson, index) => renderCard(lesson, index))}
            </div>
          );
        })()}

        {/* Lesson Detail Modal */}
        {selectedLesson && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/85 backdrop-blur-md"
            onClick={() => setSelectedLesson(null)}
          >
            <motion.article
              initial={{ scale: 0.95, opacity: 0, y: 12 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 22 }}
              className="relative bg-[#0E0E10] border border-white/10 rounded-3xl shadow-2xl shadow-purple-900/20 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              {/* Close button - sticky top-right */}
              <button
                onClick={() => setSelectedLesson(null)}
                className="sticky top-4 float-right z-10 mr-4 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 backdrop-blur flex items-center justify-center text-zinc-300 hover:text-white transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Hero header with gradient */}
              <header className="relative px-8 sm:px-12 pt-12 pb-10 border-b border-white/5 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/15 via-purple-500/5 to-transparent pointer-events-none" />
                <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-purple-500/10 blur-3xl pointer-events-none" />

                <div className="relative">
                  <div className="flex items-center gap-3 mb-5 text-xs uppercase tracking-[0.18em] font-semibold text-purple-300">
                    <span className="px-2.5 py-1 rounded-full bg-purple-500/15 border border-purple-500/20">
                      {t(`theory.categories.${selectedLesson.category}`)}
                    </span>
                    <span className="text-zinc-600">·</span>
                    <span className={`px-2.5 py-1 rounded-full border ${
                      selectedLesson.difficulty === 'beginner' ? 'bg-green-500/10 border-green-500/20 text-green-400' :
                      selectedLesson.difficulty === 'intermediate' ? 'bg-blue/10 border-blue/20 text-blue' :
                      'bg-amber/10 border-amber/20 text-amber'
                    }`}>
                      {t(`levels.${selectedLesson.difficulty}`)}
                    </span>
                    {selectedLesson.duration_minutes && (
                      <>
                        <span className="text-zinc-600">·</span>
                        <span className="flex items-center gap-1 text-zinc-400 normal-case tracking-normal">
                          <Clock className="w-3.5 h-3.5" />
                          {t('theory.duration', { minutes: selectedLesson.duration_minutes })}
                        </span>
                      </>
                    )}
                  </div>

                  <div className="flex items-start gap-6">
                    <div className="hidden sm:flex shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/30 to-purple-700/20 border border-purple-500/30 items-center justify-center font-heading text-3xl font-black text-purple-100">
                      {selectedLesson.order}
                    </div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black text-white leading-tight tracking-tight">
                      {getTitle(selectedLesson)}
                    </h1>
                  </div>
                </div>
              </header>

              {/* Body */}
              <div className="px-8 sm:px-12 py-10">
                {lessonLoading ? (
                  <div className="flex justify-center py-16">
                    <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <>
                    {/* Objectives box */}
                    {getObjectives(selectedLesson).length > 0 && (
                      <CollapsibleSection
                        wrap="mb-10 rounded-2xl bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/20 p-6"
                        header="text-purple-300"
                        chevron="text-purple-300"
                        icon={Target}
                        title={t('theory.objectives')}
                        testId="section-objectives"
                      >
                        <ul className="space-y-2.5">
                          {getObjectives(selectedLesson).map((obj, i) => (
                            <li key={i} className="flex items-start gap-3 text-zinc-200">
                              <span className="shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-purple-400" />
                              <span className="leading-relaxed">{obj}</span>
                            </li>
                          ))}
                        </ul>
                      </CollapsibleSection>
                    )}

                    {/* Markdown content - split into collapsible H2 sections */}
                    {(() => {
                      const { intro, sections } = splitMarkdownByH2(getContent(selectedLesson) || '');
                      return (
                        <div className="text-zinc-300 text-[17px] leading-[1.75]">
                          {intro && (
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                              {intro}
                            </ReactMarkdown>
                          )}
                          {sections.map((s, i) => (
                            <MarkdownSection key={i} title={s.title} index={i}>
                              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                                {s.body}
                              </ReactMarkdown>
                            </MarkdownSection>
                          ))}
                        </div>
                      );
                    })()}

                    {/* Common mistakes box */}
                    {getCommonMistakes(selectedLesson).length > 0 && (
                      <CollapsibleSection
                        wrap="mt-10 rounded-2xl bg-amber/5 border border-amber/20 p-6"
                        header="text-amber"
                        chevron="text-amber"
                        icon={AlertTriangle}
                        title={t('theory.commonMistakes')}
                        testId="section-mistakes"
                      >
                        <ul className="space-y-3">
                          {getCommonMistakes(selectedLesson).map((m, i) => (
                            <li key={i} className="flex items-start gap-3 text-zinc-200">
                              <span className="shrink-0 mt-0.5 w-6 h-6 rounded-full bg-amber/15 text-amber font-mono text-xs font-bold flex items-center justify-center">
                                {i + 1}
                              </span>
                              <span className="leading-relaxed">{m}</span>
                            </li>
                          ))}
                        </ul>
                      </CollapsibleSection>
                    )}

                    {/* Checklist box */}
                    {getChecklist(selectedLesson).length > 0 && (
                      <CollapsibleSection
                        wrap="mt-6 rounded-2xl bg-green-500/5 border border-green-500/20 p-6"
                        header="text-green-400"
                        chevron="text-green-400"
                        icon={ListChecks}
                        title={t('theory.checklist')}
                        testId="section-checklist"
                      >
                        <ul className="space-y-3">
                          {getChecklist(selectedLesson).map((item, i) => (
                            <li key={i} className="flex items-start gap-3 text-zinc-200">
                              <span className="shrink-0 mt-0.5 w-5 h-5 rounded-md border-2 border-green-500/40 bg-green-500/5 flex items-center justify-center">
                                <Check className="w-3 h-3 text-green-400/0" />
                              </span>
                              <span className="leading-relaxed">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </CollapsibleSection>
                    )}

                    {/* Esercizi consigliati */}
                    {(selectedLesson.assigned_exercises || []).length > 0 && (
                      <CollapsibleSection
                        wrap="mt-6 rounded-2xl bg-purple-500/5 border border-purple-500/20 p-6"
                        header="text-purple-300"
                        chevron="text-purple-300"
                        icon={Dumbbell}
                        title={t('theory.assignedExercises')}
                        testId="section-exercises"
                      >
                        <div className="flex flex-wrap gap-2">
                          {(selectedLesson.assigned_exercises || []).map((exId) => {
                            const ex = exercisesById[exId];
                            const title = ex ? (i18n.language === 'it' ? ex.title_it : ex.title) || ex.title : exId;
                            return (
                              <Link
                                key={exId}
                                to={`/exercises?focus=${exId}`}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-100 hover:bg-purple-500/20 text-sm transition-colors"
                              >
                                <Dumbbell className="w-3.5 h-3.5" />
                                <span>{title}</span>
                                <ChevronRight className="w-3.5 h-3.5 opacity-70" />
                              </Link>
                            );
                          })}
                        </div>
                      </CollapsibleSection>
                    )}

                    {/* Brani da praticare */}
                    {(selectedLesson.assigned_songs || []).length > 0 && (
                      <CollapsibleSection
                        wrap="mt-6 rounded-2xl bg-amber/5 border border-amber/20 p-6"
                        header="text-amber"
                        chevron="text-amber"
                        icon={Music}
                        title={t('theory.assignedSongs')}
                        testId="section-songs"
                      >
                        <div className="flex flex-wrap gap-2">
                          {(selectedLesson.assigned_songs || []).map((songId) => {
                            const song = songsById[songId];
                            const title = song ? song.title : songId;
                            return (
                              <Link
                                key={songId}
                                to={`/songs?focus=${songId}`}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-amber/10 border border-amber/30 text-amber-100 hover:bg-amber/20 text-sm transition-colors"
                              >
                                <Music className="w-3.5 h-3.5" />
                                <span>{title}</span>
                                <ChevronRight className="w-3.5 h-3.5 opacity-70" />
                              </Link>
                            );
                          })}
                        </div>
                      </CollapsibleSection>
                    )}
                  </>
                )}
              </div>

              {/* Footer actions */}
              {!lessonLoading && (
                <footer className="sticky bottom-0 px-8 sm:px-12 py-5 bg-[#0E0E10]/95 backdrop-blur border-t border-white/5 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                  {!completedIds.has(selectedLesson.id) && (
                    <Button
                      onClick={(e) => markCompleted(e, selectedLesson.id)}
                      disabled={savingId === selectedLesson.id}
                      className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-black font-semibold hover:opacity-90"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      {t('theory.markCompleted')}
                    </Button>
                  )}
                  {selectedLesson.next_lesson_id && findLesson(selectedLesson.next_lesson_id) && (
                    <Button
                      variant="outline"
                      onClick={() => openLesson(findLesson(selectedLesson.next_lesson_id))}
                      className="flex-1 border-purple-500/30 text-purple-200 hover:bg-purple-500/10"
                    >
                      {t('theory.nextLesson')}
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    onClick={() => setSelectedLesson(null)}
                    className="text-zinc-400 hover:text-white"
                  >
                    {t('common.back')}
                  </Button>
                </footer>
              )}
            </motion.article>
          </motion.div>
        )}

        {/* Empty State */}
        {!loading && filteredLessons.length === 0 && (
          <div className="text-center py-20">
            <BookOpen className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">{t('theory.noResults')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TheoryPage;
